import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, styled } from "@mui/material";

import { ViewContext } from "../../context/view-context";
import {
  selectAllProductsFetched,
  selectAnalyticsStartDate,
  selectAnalyticsEndDate,
  selectIsFetchedBySearch,
  selectProductAnalyticsIds,
  selectProductListBySearch,
} from "../../store/product/ProductSelectors";
import {
  getHeatMapMetricValueForProduct,
  selectHeatMapBatchPages,
  selectHeatMapColorScales,
  selectHeatMapMetric,
  selectHeatMapResolvedDateRange,
  selectProductAnalyticsCache,
  selectShowMiniHeatMap,
} from "../../store/heatmap/HeatMapSelectors";
import {
  selectCurrentPage,
  selectProductListEditSequence,
  selectProductListSequenceMode,
  selectProductListsFetched,
} from "../../store/product-list/ProductListSelectors";
import {
  setCurrentPage,
  setPendingScrollTarget,
} from "../../store/product-list/ProductListActions";
import { loadProductAnalytics } from "../../store/product/ProductActions";
import { ProductSequenceData } from "../../store/product-list/ProductListTypes";
import { normalizeAnalyticsDate } from "../../utils/HeatMapUtils";
import { isHeatMapMetricDateReliant } from "../heat-map-modal/HeatMapMetricSelect";
import { PRODUCT_TILE_HEIGHTS } from "../../utils/Constants";
import AppEnv from "utils/AppEnv";

const PANEL_WIDTH = 132;
const CONTAINER_INNER_WIDTH = 120;
const CELL_SIZE = 12;
const CELL_MARGIN = 1;
const CELL_TOTAL = CELL_SIZE + CELL_MARGIN * 2;

const StyledPanel = styled(Box)(({ theme }) => ({
  // Inline flex-row sibling of the catalog grid — NOT a floating panel.
  // Matches legacy: the products container shrinks by ~120px and the
  // minimap occupies that slice.
  flex: `0 0 ${PANEL_WIDTH}px`,
  width: PANEL_WIDTH,
  alignSelf: "stretch",
  paddingTop: theme.spacing(1),
  paddingLeft: theme.spacing(0.75),
  paddingRight: theme.spacing(0.75),
  borderLeft: `1px solid ${theme.palette.divider}`,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  fontFamily: "Helvetica",
  boxSizing: "border-box",
}));

const StyledCellsViewport = styled(Box)({
  // Independent scrolling is disabled — mini map slides programmatically
  // (via the inner content's `transform: translateY`) when the catalog
  // grid's page changes. Mirrors legacy where the user couldn't scroll
  // the mini map directly; only the main grid drove its position.
  position: "relative",
  width: CONTAINER_INNER_WIDTH,
  flex: 1,
  overflow: "hidden",
  boxSizing: "border-box",
  cursor: "pointer",
});

const StyledScrollableContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== "translateY",
})<{ translateY: number }>(({ translateY }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  transform: `translateY(${-translateY}px)`,
  transition: "transform 200ms ease",
}));

const StyledCellsGrid = styled(Box, {
  shouldForwardProp: (prop) => prop !== "columns",
})<{ columns: number }>(({ columns }) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${columns}, ${CELL_SIZE}px)`,
  width: CELL_TOTAL * columns,
  marginLeft: "auto",
  marginRight: "auto",
  gap: `${CELL_MARGIN * 2}px`,
  paddingTop: CELL_MARGIN,
  paddingBottom: CELL_MARGIN,
}));

const StyledCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== "cellColor",
})<{ cellColor?: string }>(({ theme, cellColor }) => ({
  width: CELL_SIZE,
  height: CELL_SIZE,
  backgroundColor: cellColor ?? theme.palette.action.hover,
  boxSizing: "border-box",
}));

const StyledFocusWindow = styled(Box)({
  position: "absolute",
  left: 0,
  right: 0,
  backgroundColor: "rgba(51, 166, 204, 0.3)",
  pointerEvents: "none",
  transition: "top 200ms ease, height 200ms ease",
});


interface MiniHeatMapProps {
  gridScrollTop?: number;
}

const MiniHeatMap: React.FC<MiniHeatMapProps> = ({ gridScrollTop = 0 }) => {
  const dispatch = useDispatch();
  const show = useSelector(selectShowMiniHeatMap);
  // Column count from the same source as the planogram:
  // ViewContext reflects the merchandiser's selected catalog grid view.
  const view = useContext(ViewContext);
  const columns = Math.max(1, view.styles.column);

  const metric = useSelector(selectHeatMapMetric);
  const scales = useSelector(selectHeatMapColorScales);
  const productsById = useSelector(selectAllProductsFetched);
  const productAnalyticsCache = useSelector(selectProductAnalyticsCache);

  // Same product-source precedence the grid container uses
  // (search > view sequence > edit sequence).
  const products = useSelector(selectProductListsFetched);
  const editProducts = useSelector(selectProductListEditSequence);
  const productsBySearch = useSelector(selectProductListBySearch);
  const isFetchedBySearch = useSelector(selectIsFetchedBySearch);
  const sequenceMode = useSelector(selectProductListSequenceMode);
  const currentPage = useSelector(selectCurrentPage);
  const pageSize: number = AppEnv.PRODUCT_LIST_PAGE_SIZE ?? 24;

  const sequence = useMemo<ProductSequenceData[]>(() => {
    if (isFetchedBySearch) return productsBySearch as ProductSequenceData[];
    return (
      sequenceMode === 1 ? products : editProducts
    ) as ProductSequenceData[];
  }, [
    isFetchedBySearch,
    productsBySearch,
    sequenceMode,
    products,
    editProducts,
  ]);

  /**
   * Per-product color via the same band logic the planogram uses.
   * Built locally (not via useSelector) because returning a function
   * from useSelector would change identity each render and trigger an
   * infinite update loop.
   */
  const colorFor = useMemo(() => {
    return (productId: string): string | undefined => {
      if (!scales.length) return undefined;
      const product = productsById[productId];
      const value = getHeatMapMetricValueForProduct(
        metric,
        product,
        productAnalyticsCache,
      );
      if (value === undefined) return undefined;
      const band = scales.find(
        (s) => value <= s.upperBound && value >= s.lowerBound,
      );
      return band?.color ?? scales[scales.length - 1]?.color;
    };
  }, [metric, scales, productsById, productAnalyticsCache]);

  const mainGridRowHeight = PRODUCT_TILE_HEIGHTS.get(columns) ?? 425;

  const focusWindow = useMemo(() => {
    if (!sequence.length) return null;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(sequence.length, startIndex + pageSize);
    const startRow = Math.floor(startIndex / columns);
    const endRow = Math.ceil(endIndex / columns);
    const intraPageOffsetPx =
      (gridScrollTop / mainGridRowHeight) * CELL_TOTAL;
    return {
      top: startRow * CELL_TOTAL + CELL_MARGIN + intraPageOffsetPx,
      height: Math.max(CELL_TOTAL, (endRow - startRow) * CELL_TOTAL),
    };
  }, [
    sequence.length,
    currentPage,
    pageSize,
    columns,
    gridScrollTop,
    mainGridRowHeight,
  ]);

  const productAnalyticsIds = useSelector(selectProductAnalyticsIds);
  const resolvedDateRange = useSelector(selectHeatMapResolvedDateRange);
  const cachedStartDate = useSelector(selectAnalyticsStartDate);
  const cachedEndDate = useSelector(selectAnalyticsEndDate);
  const batchPages = useSelector(selectHeatMapBatchPages);
  const isDateReliant = isHeatMapMetricDateReliant(metric);
  const dispatchedRef = useRef<{ rangeKey: string; ids: Set<string> }>({
    rangeKey: "",
    ids: new Set<string>(),
  });

  const batchAnalyticsIds = useMemo(() => {
    const set = new Set<string>();
    batchPages.pages.forEach((page) => {
      page.productIds.forEach((productId) => {
        const analyticsId = productsById[productId]?.analyticsId;
        if (analyticsId) set.add(analyticsId);
      });
    });
    return set;
  }, [batchPages, productsById]);

  useEffect(() => {
    if (!show) return;
    if (!isDateReliant) return;
    if (!resolvedDateRange.startDate || !resolvedDateRange.endDate) return;
    if (productAnalyticsIds.length === 0) return;

    const rangeKey = `${normalizeAnalyticsDate(
      resolvedDateRange.startDate,
    )}|${normalizeAnalyticsDate(resolvedDateRange.endDate)}`;
    if (rangeKey !== dispatchedRef.current.rangeKey) {
      dispatchedRef.current = { rangeKey, ids: new Set<string>() };
    }

    const cacheIsFreshForRange =
      normalizeAnalyticsDate(resolvedDateRange.startDate) ===
        normalizeAnalyticsDate(cachedStartDate) &&
      normalizeAnalyticsDate(resolvedDateRange.endDate) ===
        normalizeAnalyticsDate(cachedEndDate);

    const dispatchedIds = dispatchedRef.current.ids;
    const newIds = Array.from(
      new Set(
        productAnalyticsIds.filter(
          (id) =>
            !(cacheIsFreshForRange && productAnalyticsCache[id]) &&
            !dispatchedIds.has(id) &&
            // Modal's effect handles the batch — skip those here.
            !batchAnalyticsIds.has(id),
        ),
      ),
    );
    if (newIds.length === 0) return;

    newIds.forEach((id) => dispatchedIds.add(id));
    dispatch(
      loadProductAnalytics({
        startDate: resolvedDateRange.startDate,
        endDate: resolvedDateRange.endDate,
        productCodes: newIds,
      }),
    );
  }, [
    show,
    isDateReliant,
    resolvedDateRange.startDate,
    resolvedDateRange.endDate,
    productAnalyticsIds,
    productAnalyticsCache,
    cachedStartDate,
    cachedEndDate,
    batchAnalyticsIds,
    dispatch,
  ]);

  const viewportRef = useRef<HTMLDivElement>(null);
  const [contentOffset, setContentOffset] = useState(0);

  useLayoutEffect(() => {
    if (!show) return;
    if (!viewportRef.current) return;
    if (!focusWindow) return;
    if (!sequence.length) return;
    const viewportHeight = viewportRef.current.clientHeight;
    const totalRows = Math.ceil(sequence.length / columns);
    const contentHeight = totalRows * CELL_TOTAL + CELL_MARGIN * 2;

    if (contentHeight <= viewportHeight) {
      // All cells fit — no scrolling needed.
      setContentOffset(0);
      return;
    }

    // Centre the focus window vertically in the viewport, clamped so we
    // never translate above the top or below the bottom of the content.
    const focusCentre = focusWindow.top + focusWindow.height / 2;
    const desired = focusCentre - viewportHeight / 2;
    const clamped = Math.max(
      0,
      Math.min(contentHeight - viewportHeight, desired),
    );
    setContentOffset(clamped);
  }, [show, currentPage, focusWindow, sequence.length, columns, gridScrollTop]);

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const node = viewportRef.current;
    if (!node || !sequence.length) return;
    const rect = node.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top + contentOffset;
    const row = Math.max(0, Math.floor(clickY / CELL_TOTAL));
    const gridWidth = CELL_TOTAL * columns;
    const gridLeft = Math.max(0, (CONTAINER_INNER_WIDTH - gridWidth) / 2);
    const col = Math.max(
      0,
      Math.min(columns - 1, Math.floor((clickX - gridLeft) / CELL_TOTAL)),
    );
    const productIndex = Math.min(sequence.length - 1, row * columns + col);
    const targetProduct = sequence[productIndex];
    const targetPage = Math.floor(productIndex / pageSize) + 1;
    if (targetPage !== currentPage) {
      dispatch(setCurrentPage(targetPage));
    }
    
    if (targetProduct?.productId) {
      dispatch(setPendingScrollTarget(targetProduct.productId));
    }
  };

  if (!show) return null;
  if (!sequence.length) return null;

  return (
    <StyledPanel>
      <StyledCellsViewport ref={viewportRef} onClick={handleContainerClick}>
        <StyledScrollableContent translateY={contentOffset}>
          {focusWindow && (
            <StyledFocusWindow
              sx={{ top: focusWindow.top, height: focusWindow.height }}
            />
          )}
          <StyledCellsGrid columns={columns}>
            {sequence.map((item) => (
              <StyledCell
                key={item.productId}
                cellColor={colorFor(item.productId)}
                data-product-id={item.productId}
              />
            ))}
          </StyledCellsGrid>
        </StyledScrollableContent>
      </StyledCellsViewport>
    </StyledPanel>
  );
};

export default MiniHeatMap;
